import { createFileRoute } from "@tanstack/react-router";
import { CampaignLanding, type FaqItem } from "@/components/sections/CampaignLanding";
import { TikTokPixel } from "@/components/analytics/TikTokPixel";

const URL = "https://kentro.com.br/tiktok";
const TITLE =
  "Kentro Sistemas Inteligentes | Atendimento com IA para quem vende online";
const DESC =
  "Viralizou e o WhatsApp travou? A Kentro atende com IA, organiza o CRM e converte audiência em venda. Chat + CRM + IA em uma só plataforma.";

const QUICK_BULLETS = [
  "Aguenta picos de viralização sem travar",
  "IA atende WhatsApp e Direct 24/7",
  "Recuperação automática de carrinho",
  "Funil de vendas no CRM da Kentro",
  "API Oficial — sem risco de bloqueio",
  "Sem fidelidade, cancele quando quiser",
];

const FAQS: FaqItem[] = [
  {
    q: "Meu vídeo viralizou e o WhatsApp travou. A Kentro resolve?",
    a: "Sim. A IA atende em segundos mesmo com volume alto e organiza cada lead no CRM, para o time humano focar nos contatos quentes.",
  },
  {
    q: "Funciona para infoprodutor e e-commerce?",
    a: "Funciona. Atendemos infoprodutores, e-commerces, prestadores de serviço e operações que escalam rápido por redes sociais.",
  },
  {
    q: "Tem risco de bloquear meu WhatsApp?",
    a: "Não. A Kentro usa a API Oficial do WhatsApp Business, com regras de conformidade — sem risco de banimento por automação.",
  },
  {
    q: "Consigo integrar com meu checkout / plataforma?",
    a: "Sim. Integramos com as principais plataformas de checkout, ERPs e CRMs do mercado, além de webhooks personalizados.",
  },
  {
    q: "Quanto custa?",
    a: "Depende do volume e dos canais. Fale com um especialista pelo WhatsApp para receber proposta sob medida — a demonstração é gratuita.",
  },
];

export const Route = createFileRoute("/tiktok")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Kentro: IA + Chat + CRM para escalar vendas" },
      {
        property: "og:description",
        content:
          "Vendas escalam, atendimento também. IA, CRM e omnichannel para times que crescem rápido.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Kentro Sistemas Inteligentes",
            url: "https://kentro.com.br",
            logo: "https://kentro.com.br/og-image.png",
            sameAs: [
              "https://www.linkedin.com/company/kentro-sistemas",
              "https://www.instagram.com/kentrosistemas",
              "https://www.youtube.com/@kentrosistemas",
              "https://www.tiktok.com/@kentrooficial",
            ],
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: "+55-0800-550-2020",
                contactType: "customer service",
                areaServed: "BR",
                availableLanguage: ["Portuguese"],
              },
              {
                "@type": "ContactPoint",
                telephone: "+55-11-91582-7716",
                contactType: "sales",
                areaServed: "BR",
                availableLanguage: ["Portuguese"],
              },
              {
                "@type": "ContactPoint",
                telephone: "+55-38-3220-8706",
                contactType: "sales",
                areaServed: "BR",
                availableLanguage: ["Portuguese"],
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: TITLE,
            description: DESC,
            url: URL,
            inLanguage: "pt-BR",
            isPartOf: {
              "@type": "WebSite",
              name: "Kentro Sistemas Inteligentes",
              url: "https://kentro.com.br",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]),
      },
    ],
  }),
  component: TikTokLanding,
});

function TikTokLanding() {
  return (
    <>
      <TikTokPixel pixelId="D9F9U1JC77U79CKF7H30" />
      <CampaignLanding
        source="tiktok"
      eyebrow="Veio do TikTok?"
      headline={
        <>
          Sua audiência cresceu. Seu atendimento aguenta?{" "}
          <span className="gradient-text">Chat + CRM + IA</span>
        </>
      }
      subheadline="A Kentro atende WhatsApp, Direct, voz e e-mail com IA treinada para o seu negócio — para você escalar venda sem dobrar a equipe."
      highlights={[
        "IA responde em segundos, mesmo quando o volume explode.",
        "Cada conversa vira lead no CRM com histórico completo.",
        "WhatsApp API Oficial — sem risco de bloqueio.",
        "Automações para recuperar carrinho e fechar venda no DM.",
        "Relatórios mostram qual conteúdo realmente vende.",
      ]}
      quickBullets={QUICK_BULLETS}
      faqs={FAQS}
      whatsappMessage="Olá! Vim pelo TikTok e quero saber mais."
      />
    </>
  );
}
